/**
 * 銷貨折讓單(REQ-20260821001)。
 *
 * 需求原文:「如果遇到跨月改帳,我們可以原帳不動,直接開一筆『折讓單』記錄
 * 要退給客戶的金額,並在下個月產生該客戶的月結對帳單時,讓系統自動在總額中扣除。」
 *
 * **與 `sales_orders.allowanceAmount`(REQ-20260806002 的單頭折讓)分工不同:**
 *  - `allowanceAmount`:單頭的一次性**覆寫**欄位,直接扣那張單的未結餘額。
 *  - 折讓單:可累積多筆、有單號 / 開立日 / 套用期別的憑證,只影響月結對帳單
 *    那一期的「本期折讓」,**不動出貨單本身的未結餘額**。
 *
 * 兩者的金額來自不同地方(欄位 vs 這張表),所以對帳單不會把同一筆折讓算兩次。
 */
import { z } from 'zod';
import { positiveDecimalString } from './decimal.schema';

/** POST /sales-orders/:id/credit-notes body */
export const CreateSalesOrderCreditNoteSchema = z.object({
  /** 折讓金額(正數)。對帳單上是扣除,不用負數表示 */
  amount: positiveDecimalString,
  reason: z.string().trim().min(1, '請填寫折讓原因').max(255),
  /** 開立日期 YYYY-MM-DD(台北日期) */
  issuedDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, '開立日期格式應為 YYYY-MM-DD'),
  /**
   * 套用期別 YYYY-MM。**留空 = 開立日的次月**(需求原文的「下個月的對帳單扣除」)。
   * 想當期就扣的話明確填當月,不要靠系統猜。
   */
  appliedPeriod: z
    .string()
    .regex(/^\d{4}-\d{2}$/, '套用期別格式應為 YYYY-MM')
    .optional()
    .nullable(),
});
export type CreateSalesOrderCreditNoteInput = z.infer<typeof CreateSalesOrderCreditNoteSchema>;

/** 一張折讓單(列表 / 建立後的回應) */
export interface SalesOrderCreditNoteItem {
  id: number;
  uuid: string;
  documentNo: string;
  salesOrderId: number;
  /** 來源出貨單的單號 —— 前端要顯示「折讓的是哪一張單」 */
  orderNo: string;
  customerId: number;
  amount: string;
  reason: string;
  issuedDate: string;
  /** 使用者指定的套用期別;沒指定就是 null */
  appliedPeriod: string | null;
  /**
   * 實際生效的期別 = `appliedPeriod ?? 開立日的次月`。
   * 後端算好給前端顯示,免得畫面上再實作一次同樣的推算規則。
   */
  effectivePeriod: string;
  /**
   * 審核狀態(REQ-20260821002)。需求原文是「折讓單建立**並審核後**才標記為
   * 該客戶的未實現扣抵款」—— 所以**只有 `REVIEWED` 會被月結對帳單扣除**,
   * `PENDING` 只是開出來還沒生效的憑證。
   */
  status: 'PENDING' | 'REVIEWED';
  reviewedByName: string | null;
  reviewedAt: string | null;
  createdByName: string | null;
  createdAt: string;
}

export interface SalesOrderCreditNoteListResponse {
  items: SalesOrderCreditNoteItem[];
  /** 這批折讓單的金額合計 */
  totalAmount: string;
}
